import type { PackageArt, Product, ProductSize } from '@/types'

import { applyDiscount } from '@/lib/format'

export const sortedSizes = (product: Product): Array<ProductSize> =>
    [...product.sizes].sort((a, b) => a.sort_order - b.sort_order || a.price - b.price)

export const defaultSize = (product: Product): ProductSize | null => {
    const sizes = sortedSizes(product)

    return sizes.find(size => size.stock > 0) ?? sizes[0] ?? null
}

export const totalStock = (product: Product): number =>
    product.sizes.reduce((sum, size) => sum + Math.max(0, size.stock), 0)

export const lowestPrice = (product: Product): number => {
    const prices = product.sizes.map(size => size.price)

    return prices.length ? Math.min(...prices) : 0
}

export const lowestFinalPrice = (product: Product): number =>
    applyDiscount(lowestPrice(product), product.discount_percent)

export const hasCategory = (product: Product, slug: string): boolean =>
    product.categories.some(category => category.slug === slug)

export const primaryCategory = (product: Product) =>
    product.categories.find(category => !['produk-terbaru', 'produk-terlaris', 'paket-hemat'].includes(category.slug)) ?? product.categories[0] ?? null

export const packageArt = (product: Product, size?: ProductSize | null): PackageArt => ({
    id: product.id,
    name: product.name,
    flavor: product.flavor,
    tagline: product.tagline,
    composition: product.composition,
    sizeLabel: (size ?? defaultSize(product))?.label ?? null,
    packageColor: product.package_color,
    accentColor: product.accent_color,
    image: product.image
})

export const savingsFor = (product: Product, size?: ProductSize | null): number => {
    const target = size ?? defaultSize(product)

    if (!target) return 0

    return target.price - applyDiscount(target.price, product.discount_percent)
}

export const flavorList = (products: Array<Product>): Array<string> => {
    const set = new Set<string>()

    products.forEach(product => {
        if (product.flavor) set.add(product.flavor)
    })

    return [...set]
}
